import React, { useMemo, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemText,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import {
	cardSx,
	compactDateSlotProps,
	compactOptionalFieldSx,
	compactTextFieldSx,
	primaryButtonSx,
} from "../sharedStyles";

const VERDICT_META = {
	active: {
		icon: CheckCircleOutlineIcon,
		color: "#2e7d32",
		bgcolor: "rgba(46, 125, 50, 0.08)",
		borderColor: "rgba(46, 125, 50, 0.35)",
	},
	not_active: {
		icon: HelpOutlineOutlinedIcon,
		color: "#ed6c02",
		bgcolor: "rgba(237, 108, 2, 0.08)",
		borderColor: "rgba(237, 108, 2, 0.35)",
	},
	not_found: {
		icon: HighlightOffOutlinedIcon,
		color: "#0288d1",
		bgcolor: "rgba(2, 136, 209, 0.08)",
		borderColor: "rgba(2, 136, 209, 0.35)",
	},
};

const accordionSx = {
	...cardSx,
	"&:before": { display: "none" },
	overflow: "hidden",
};

function parseStoresList(storesValue) {
	if (!storesValue) {
		return [];
	}
	if (Array.isArray(storesValue)) {
		const seen = new Set();
		return storesValue
			.map((store) => String(store || "").trim())
			.filter((store) => {
				if (!store) {
					return false;
				}
				const key = store.toLowerCase();
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});
	}

	let text = String(storesValue).trim();
	if (!text || text.toLowerCase() === "nan") {
		return [];
	}

	// Handle Python/JSON list strings: "['A', 'B']" or '["A","B"]'
	if (
		(text.startsWith("[") && text.endsWith("]")) ||
		(text.startsWith("(") && text.endsWith(")"))
	) {
		try {
			const normalized = text
				.replace(/'/g, '"')
				.replace(/\bNone\b/g, "null")
				.replace(/\bTrue\b/g, "true")
				.replace(/\bFalse\b/g, "false");
			const parsed = JSON.parse(normalized);
			if (Array.isArray(parsed)) {
				return parseStoresList(parsed);
			}
		} catch (_error) {
			// Fall through to delimiter split.
		}
	}

	const seen = new Set();
	return text
		.split(/[,/|;]+|\|\|/)
		.map((store) => store.trim().replace(/^[[({'"\s]+|[\]})'"\s]+$/g, "").trim())
		.filter((store) => {
			if (!store || store.toLowerCase() === "nan") {
				return false;
			}
			const key = store.toLowerCase();
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
}

function StoresDialog({ open, onClose, title, stores = [] }) {
	const [search, setSearch] = useState("");

	const filteredStores = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) {
			return stores;
		}
		return stores.filter((store) => store.toLowerCase().includes(query));
	}, [search, stores]);

	const handleClose = () => {
		setSearch("");
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{ sx: { borderRadius: 3 } }}
		>
			<DialogTitle sx={{ pr: 6 }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<StoreOutlinedIcon color="action" />
					<Box minWidth={0}>
						<Typography fontWeight={700}>Stores</Typography>
						{title ? (
							<Typography variant="caption" color="text.secondary" noWrap>
								{title}
							</Typography>
						) : null}
					</Box>
				</Stack>
				<IconButton
					aria-label="Close stores dialog"
					onClick={handleClose}
					sx={{ position: "absolute", right: 12, top: 12 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ pt: 2 }}>
				<TextField
					fullWidth
					size="small"
					placeholder="Search stores..."
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchOutlinedIcon fontSize="small" />
							</InputAdornment>
						),
					}}
					sx={{ mb: 2 }}
				/>
				<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
					{filteredStores.length} of {stores.length} store
					{stores.length === 1 ? "" : "s"}
				</Typography>
				{filteredStores.length ? (
					<List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
						{filteredStores.map((store) => (
							<ListItem key={store} divider>
								<ListItemText primary={store} />
							</ListItem>
						))}
					</List>
				) : (
					<Alert severity="info" sx={{ borderRadius: 2 }}>
						{stores.length
							? "No stores match your search."
							: "No stores linked to this discount."}
					</Alert>
				)}
			</DialogContent>
		</Dialog>
	);
}

function OfferResultCard({ offer, active }) {
	return (
		<Paper
			elevation={0}
			sx={{
				...cardSx,
				p: 2,
				borderWidth: 2,
				borderColor: active ? "rgba(46, 125, 50, 0.45)" : "#e8e8e8",
				bgcolor: active ? "rgba(46, 125, 50, 0.05)" : "#fff",
			}}
		>
			<Stack spacing={1.25}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					gap={1}
					flexWrap="wrap"
				>
					<Box minWidth={0} flex={1}>
						<Chip
							icon={<LocalOfferOutlinedIcon />}
							label={offer.offer_text || offer.scheme_name || "Offer"}
							sx={{
								height: "auto",
								py: 0.85,
								mb: 0.75,
								maxWidth: "100%",
								fontWeight: 700,
								bgcolor: active ? "#1b5e20" : "#212121",
								color: "#fff",
								"& .MuiChip-icon": { color: "#fff" },
								"& .MuiChip-label": {
									whiteSpace: "normal",
									display: "block",
									lineHeight: 1.35,
								},
							}}
						/>
						{offer.scheme_name && offer.scheme_name !== offer.offer_text ? (
							<Typography variant="caption" color="text.secondary" display="block">
								Scheme: {offer.scheme_name}
							</Typography>
						) : null}
					</Box>
					<Chip
						size="small"
						label={active ? "Active" : "Not active"}
						color={active ? "success" : "default"}
						variant={active ? "filled" : "outlined"}
					/>
				</Stack>

				<Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
					{offer.offer_type ? (
						<Chip size="small" label={offer.offer_type} variant="outlined" />
					) : null}
					{offer.offer_value != null && offer.offer_value !== "" ? (
						<Chip
							size="small"
							variant="outlined"
							label={`Value: ${offer.offer_value}`}
						/>
					) : null}
					{(offer.valid_from || offer.valid_to) && (
						<Chip
							size="small"
							variant="outlined"
							label={`${formatDisplayDate(offer.valid_from) || "?"} → ${formatDisplayDate(offer.valid_to) || "?"}`}
						/>
					)}
					{offer.close_date ? (
						<Chip
							size="small"
							color="warning"
							variant="outlined"
							label={`Force closed: ${formatDisplayDate(offer.close_date)}`}
						/>
					) : null}
					{offer.brand ? (
						<Chip
							size="small"
							variant="outlined"
							label={`Brand: ${formatListLikeText(offer.brand)}`}
						/>
					) : null}
				</Stack>

				{!!offer.applicable_days?.length && (
					<Typography variant="body2" color="text.secondary">
						Applicable days: {offer.applicable_days.join(", ")}
					</Typography>
				)}

				{!!offer.store_codes?.length && (
					<Typography variant="body2" color="text.secondary">
						Stores: {offer.store_codes.slice(0, 8).join(", ")}
						{offer.store_codes.length > 8
							? ` +${offer.store_codes.length - 8} more`
							: ""}
					</Typography>
				)}

				{!!offer.blockers?.length && (
					<Alert severity="warning" sx={{ py: 0.5, borderRadius: 2 }}>
						{offer.blockers.join(" · ")}
					</Alert>
				)}

				{!!offer.notes?.length && (
					<Typography variant="caption" color="text.secondary">
						{offer.notes.join(" · ")}
					</Typography>
				)}
			</Stack>
		</Paper>
	);
}

function formatDisplayDate(value) {
	if (!value) {
		return "";
	}
	const text = String(value).trim();
	if (!text) {
		return "";
	}
	const parsed = dayjs(text);
	if (parsed.isValid()) {
		return parsed.format("DD MMM YYYY");
	}
	return text.length >= 10 ? text.slice(0, 10) : text;
}

function parseSortDate(value) {
	if (!value) {
		return 0;
	}
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.valueOf() : 0;
}

function sortSchemesLatestFirst(schemes) {
	return [...(schemes || [])].sort((a, b) => {
		const createdDiff =
			parseSortDate(b.created_at) - parseSortDate(a.created_at);
		if (createdDiff !== 0) {
			return createdDiff;
		}
		const startDiff =
			parseSortDate(b.start_date) - parseSortDate(a.start_date);
		if (startDiff !== 0) {
			return startDiff;
		}
		return Number(b.discount_id || 0) - Number(a.discount_id || 0);
	});
}

function SchemeDateBadge({ label, value, accent = "primary" }) {
	const display = formatDisplayDate(value);
	if (!display) {
		return null;
	}

	const colors =
		accent === "end"
			? {
					bgcolor: "rgba(237, 108, 2, 0.12)",
					borderColor: "rgba(237, 108, 2, 0.45)",
					color: "#e65100",
			  }
			: {
					bgcolor: "rgba(25, 118, 210, 0.1)",
					borderColor: "rgba(25, 118, 210, 0.4)",
					color: "#1565c0",
			  };

	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.75,
				px: 1.25,
				py: 0.75,
				borderRadius: 1.5,
				border: `1px solid ${colors.borderColor}`,
				bgcolor: colors.bgcolor,
				color: colors.color,
				minWidth: 0,
			}}
		>
			<CalendarMonthOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
			<Box minWidth={0}>
				<Typography
					variant="caption"
					fontWeight={700}
					sx={{ display: "block", lineHeight: 1.1, opacity: 0.85 }}
				>
					{label}
				</Typography>
				<Typography variant="body2" fontWeight={700} lineHeight={1.25}>
					{display}
				</Typography>
			</Box>
		</Box>
	);
}

function formatListLikeText(value) {
	const parts = parseStoresList(value);
	if (parts.length) {
		return parts.join(", ");
	}
	const text = String(value || "").trim();
	if (!text || text.toLowerCase() === "nan") {
		return "";
	}
	return text.replace(/^[[({'"\s]+|[\]})'"\s]+$/g, "").trim();
}

function parseOfferDisplayList(displayValue) {
	if (!displayValue) {
		return [];
	}
	if (Array.isArray(displayValue)) {
		return parseStoresList(displayValue);
	}
	const seen = new Set();
	return String(displayValue)
		.split(/\s*\|\s*/)
		.map((part) => part.trim())
		.filter((part) => {
			if (!part) {
				return false;
			}
			const key = part.toLowerCase();
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
}

function SchemeRunningCard({ scheme }) {
	const [storesOpen, setStoresOpen] = useState(false);
	const stores = useMemo(() => parseStoresList(scheme.stores), [scheme.stores]);
	const offers = useMemo(
		() => parseOfferDisplayList(scheme.display_strings),
		[scheme.display_strings]
	);
	const schemeTitle = scheme.scheme_name || `Discount #${scheme.discount_id}`;
	const isPublished =
		String(scheme.status || "").toLowerCase() === "published";

	return (
		<Paper
			elevation={0}
			sx={{
				...cardSx,
				p: 2,
				borderColor: isPublished
					? "rgba(46, 125, 50, 0.35)"
					: "rgba(237, 108, 2, 0.35)",
				bgcolor: isPublished
					? "rgba(46, 125, 50, 0.03)"
					: "rgba(237, 108, 2, 0.03)",
			}}
		>
			<Stack spacing={1.25}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					gap={1}
					flexWrap="wrap"
				>
					<Box minWidth={0}>
						<Typography fontWeight={700}>{schemeTitle}</Typography>
					</Box>
					<Chip
						size="small"
						label={scheme.status || "Unknown"}
						color={isPublished ? "success" : "warning"}
						variant={isPublished ? "filled" : "outlined"}
					/>
				</Stack>

				{offers.length ? (
					<Box
						sx={{
							p: 1.5,
							borderRadius: 2,
							border: "1px solid rgba(33, 33, 33, 0.12)",
							bgcolor: "#fff",
						}}
					>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{ display: "block", mb: 1 }}
						>
							Offers ({offers.length})
						</Typography>
						<Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
							{offers.map((offer) => (
								<Chip
									key={offer}
									icon={<LocalOfferOutlinedIcon />}
									label={offer}
									color="primary"
									variant="filled"
									sx={{
										height: "auto",
										py: 0.75,
										fontWeight: 600,
										bgcolor: "#212121",
										color: "#fff",
										"& .MuiChip-icon": { color: "#fff" },
										"& .MuiChip-label": {
											whiteSpace: "normal",
											display: "block",
											lineHeight: 1.35,
										},
									}}
								/>
							))}
						</Stack>
					</Box>
				) : (
					<Alert severity="info" sx={{ py: 0.5, borderRadius: 2 }}>
						No offer text found for this discount.
					</Alert>
				)}

				<Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
					{scheme.brand ? (
						<Chip
							size="small"
							variant="outlined"
							label={`Brand: ${formatListLikeText(scheme.brand)}`}
						/>
					) : null}
					{scheme.close_date ? (
						<Chip
							size="small"
							color="warning"
							variant="outlined"
							label={`Force closed: ${formatDisplayDate(scheme.close_date)}`}
						/>
					) : null}
					{scheme.created_at ? (
						<Chip
							size="small"
							variant="outlined"
							label={`Created: ${formatDisplayDate(scheme.created_at)}`}
						/>
					) : null}
					{stores.length ? (
						<Chip
							size="small"
							icon={<StoreOutlinedIcon />}
							label={`Stores (${stores.length})`}
							clickable
							onClick={() => setStoresOpen(true)}
							variant="outlined"
							sx={{ cursor: "pointer" }}
						/>
					) : null}
				</Stack>

				{(scheme.start_date || scheme.end_date) && (
					<Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
						<SchemeDateBadge label="Start date" value={scheme.start_date} />
						<SchemeDateBadge
							label="End date"
							value={scheme.end_date}
							accent="end"
						/>
					</Stack>
				)}

				{!!scheme.applicable_days?.length && (
					<Typography variant="body2" color="text.secondary">
						Applicable days: {scheme.applicable_days.join(", ")}
					</Typography>
				)}
			</Stack>

			<StoresDialog
				open={storesOpen}
				onClose={() => setStoresOpen(false)}
				title={schemeTitle}
				stores={stores}
			/>
		</Paper>
	);
}

function VerdictHero({ result }) {
	const verdictMeta = VERDICT_META[result?.verdict] || VERDICT_META.not_found;
	const VerdictIcon = verdictMeta.icon;

	return (
		<Paper
			elevation={0}
			sx={{
				...cardSx,
				p: 2.5,
				borderColor: verdictMeta.borderColor,
				bgcolor: verdictMeta.bgcolor,
			}}
		>
			<Stack spacing={1.5}>
				<Stack direction="row" spacing={1.25} alignItems="center">
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: 2,
							display: "grid",
							placeItems: "center",
							bgcolor: "#fff",
							color: verdictMeta.color,
						}}
					>
						<VerdictIcon />
					</Box>
					<Box>
						<Typography variant="h6" fontWeight={700}>
							{result.verdict_label}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Lookup result for this barcode on the selected bill date
						</Typography>
					</Box>
				</Stack>

				<Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
					<Chip
						icon={<QrCodeScannerOutlinedIcon />}
						size="small"
						label={`Barcode ${result.barcode}`}
						variant="outlined"
					/>
					{result.brand ? (
						<Chip
							size="small"
							label={`Brand ${formatListLikeText(result.brand)}`}
							variant="outlined"
						/>
					) : null}
					<Chip
						icon={<CalendarMonthOutlinedIcon />}
						size="small"
						label={`${result.bill_date}${result.bill_day ? ` (${result.bill_day})` : ""}`}
						variant="outlined"
					/>
					{result.store_code ? (
						<Chip
							icon={<StoreOutlinedIcon />}
							size="small"
							label={`Store ${result.store_code}`}
							variant="outlined"
						/>
					) : null}
				</Stack>

				<Grid container spacing={1.5}>
					<Grid item xs={4}>
						<Box
							sx={{
								p: 1.5,
								borderRadius: 2,
								border: "1px solid #ececec",
								bgcolor: "#fff",
								textAlign: "center",
							}}
						>
							<Typography variant="h6" fontWeight={700}>
								{result.counts?.total || 0}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Total offers
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={4}>
						<Box
							sx={{
								p: 1.5,
								borderRadius: 2,
								border: "1px solid rgba(46, 125, 50, 0.25)",
								bgcolor: "rgba(46, 125, 50, 0.04)",
								textAlign: "center",
							}}
						>
							<Typography variant="h6" fontWeight={700} color="success.main">
								{result.counts?.active || 0}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Active
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={4}>
						<Box
							sx={{
								p: 1.5,
								borderRadius: 2,
								border: "1px solid #ececec",
								bgcolor: "#fff",
								textAlign: "center",
							}}
						>
							<Typography variant="h6" fontWeight={700}>
								{result.counts?.not_active || 0}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Not active
							</Typography>
						</Box>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
}

function EmptyResultsState() {
	return (
		<Box
			sx={{
				py: 5,
				px: 3,
				textAlign: "center",
				borderRadius: 3,
				border: "1px dashed #e0e0e0",
				bgcolor: "rgba(0, 0, 0, 0.02)",
			}}
		>
			<Box
				sx={{
					width: 56,
					height: 56,
					mx: "auto",
					mb: 1.5,
					borderRadius: "50%",
					display: "grid",
					placeItems: "center",
					bgcolor: "rgba(33, 33, 33, 0.06)",
					color: "#212121",
				}}
			>
				<LocalOfferOutlinedIcon />
			</Box>
			<Typography variant="subtitle1" fontWeight={600} gutterBottom>
				Check which offer applies
			</Typography>
			<Typography variant="body2" color="text.secondary" maxWidth={520} mx="auto">
				Enter a barcode and bill date to see active offers, inactive offers,
				and the exact blockers (date, store, weekday, force-close) for each
				scheme.
			</Typography>
		</Box>
	);
}

function CollapsibleSection({
	title,
	description,
	icon,
	expanded,
	onChange,
	children,
}) {
	return (
		<Accordion
			expanded={expanded}
			onChange={onChange}
			disableGutters
			sx={accordionSx}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				sx={{
					px: { xs: 2, sm: 3 },
					py: 0.5,
					"& .MuiAccordionSummary-content": {
						my: 1.5,
						alignItems: "center",
						gap: 1.25,
					},
				}}
			>
				{icon}
				<Box>
					<Typography variant="h6" fontWeight={700}>
						{title}
					</Typography>
					{description ? (
						<Typography variant="body2" color="text.secondary">
							{description}
						</Typography>
					) : null}
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pt: 0 }}>
				{children}
			</AccordionDetails>
		</Accordion>
	);
}

function OfferChecker() {
	const { setSnackBar, setSnackBarOpen } = GlobalState();

	const [lookupExpanded, setLookupExpanded] = useState(false);
	const [discountExpanded, setDiscountExpanded] = useState(false);

	const [barcode, setBarcode] = useState("");
	const [billDate, setBillDate] = useState(dayjs());
	const [storeCode, setStoreCode] = useState("");
	const [storeName, setStoreName] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [result, setResult] = useState(null);

	const [discountStartDate, setDiscountStartDate] = useState(
		dayjs().subtract(30, "day")
	);
	const [discountEndDate, setDiscountEndDate] = useState(dayjs());
	const [discountLoading, setDiscountLoading] = useState(false);
	const [discountError, setDiscountError] = useState("");
	const [discountResult, setDiscountResult] = useState(null);

	const canSearch = useMemo(
		() => Boolean(barcode.trim()) && billDate?.isValid() && !loading,
		[barcode, billDate, loading]
	);

	const discountRangeError = useMemo(() => {
		if (!discountStartDate?.isValid() || !discountEndDate?.isValid()) {
			return "Select both start and end dates.";
		}
		if (discountEndDate.isBefore(discountStartDate, "day")) {
			return "End date cannot be before start date.";
		}
		return "";
	}, [discountStartDate, discountEndDate]);

	const sortedDiscountSchemes = useMemo(
		() => sortSchemesLatestFirst(discountResult?.schemes),
		[discountResult]
	);

	const canSearchDiscounts = useMemo(
		() => !discountRangeError && !discountLoading,
		[discountRangeError, discountLoading]
	);

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({
			...prev,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		}));
	};

	const handleClear = () => {
		setBarcode("");
		setBillDate(dayjs());
		setStoreCode("");
		setStoreName("");
		setError("");
		setResult(null);
	};

	const handleClearDiscounts = () => {
		setDiscountStartDate(dayjs().subtract(30, "day"));
		setDiscountEndDate(dayjs());
		setDiscountError("");
		setDiscountResult(null);
	};

	const handleSearch = async () => {
		if (!barcode.trim()) {
			setError("Enter a barcode to check.");
			return;
		}
		if (!billDate?.isValid()) {
			setError("Select a valid bill date.");
			return;
		}

		try {
			setLoading(true);
			setError("");
			setResult(null);
			const res = await pythonVmInstance.get("/lfs-payment-working/offers/lookup", {
				params: {
					barcode: barcode.trim(),
					bill_date: billDate.format("YYYY-MM-DD"),
					store_code: storeCode.trim() || undefined,
					store_name: storeName.trim() || undefined,
				},
			});
			setResult(res.data || null);
		} catch (lookupError) {
			const data = lookupError?.response?.data;
			const message =
				(typeof data === "string" && data.trim()) ||
				data?.message ||
				lookupError?.message ||
				"Failed to lookup offers";
			setResult(null);
			setError(message);
			showSnack(message, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleSearchDiscounts = async () => {
		if (discountRangeError) {
			setDiscountError(discountRangeError);
			return;
		}

		try {
			setDiscountLoading(true);
			setDiscountError("");
			setDiscountResult(null);
			const res = await pythonVmInstance.get("/lfs-payment-working/discounts", {
				params: {
					start_date: discountStartDate.format("YYYY-MM-DD"),
					end_date: discountEndDate.format("YYYY-MM-DD"),
				},
			});
			setDiscountResult(res.data || null);
		} catch (discountLookupError) {
			const data = discountLookupError?.response?.data;
			const message =
				(typeof data === "string" && data.trim()) ||
				data?.message ||
				discountLookupError?.message ||
				"Failed to load discounts for the selected date range";
			setDiscountResult(null);
			setDiscountError(message);
			showSnack(message, "error");
		} finally {
			setDiscountLoading(false);
		}
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack spacing={2.5}>
				<CollapsibleSection
					title="Offer lookup"
					description="Check whether a barcode has an active offer on a bill date."
					icon={<LocalOfferOutlinedIcon color="action" />}
					expanded={lookupExpanded}
					onChange={(_event, next) => setLookupExpanded(next)}
				>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Store fields are optional and only narrow the result to one store.
					</Typography>

					<Stack spacing={1.5}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							flexWrap="wrap"
							useFlexGap
							spacing={1.5}
							alignItems={{ xs: "stretch", sm: "center" }}
						>
							<TextField
								label="Barcode"
								value={barcode}
								onChange={(event) => {
									setBarcode(event.target.value);
									setError("");
								}}
								autoFocus
								placeholder="Scan or type barcode"
								size="small"
								variant="outlined"
								sx={compactTextFieldSx}
								onKeyDown={(event) => {
									if (event.key === "Enter" && canSearch) {
										handleSearch();
									}
								}}
							/>
							<DatePicker
								label="Bill date"
								format="DD/MM/YY"
								value={billDate}
								onChange={(value) => {
									setBillDate(value || null);
									setError("");
								}}
								slotProps={compactDateSlotProps}
							/>
							<TextField
								label="Store code"
								value={storeCode}
								onChange={(event) => setStoreCode(event.target.value)}
								size="small"
								variant="outlined"
								placeholder="Optional"
								sx={compactOptionalFieldSx}
							/>
							<TextField
								label="Store name"
								value={storeName}
								onChange={(event) => setStoreName(event.target.value)}
								size="small"
								variant="outlined"
								placeholder="Optional"
								sx={compactOptionalFieldSx}
							/>
							<Button
								variant="contained"
								size="small"
								startIcon={<SearchOutlinedIcon />}
								onClick={handleSearch}
								disabled={!canSearch}
								sx={{
									...primaryButtonSx,
									height: 40,
									px: 2,
								}}
							>
								{loading ? "Checking..." : "Check offers"}
							</Button>
							{(result || barcode || storeCode || storeName) && (
								<Button size="small" onClick={handleClear}>
									Clear
								</Button>
							)}
						</Stack>
					</Stack>

					{error ? (
						<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
							{error}
						</Alert>
					) : null}

					{loading ? (
						<Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
							<CircularProgress size={32} sx={{ mb: 1.5 }} />
							<Typography variant="body2" color="text.secondary">
								Checking offers for barcode {barcode.trim()}...
							</Typography>
						</Stack>
					) : null}

					{!loading && !result && !error ? (
						<Box sx={{ mt: 2 }}>
							<EmptyResultsState />
						</Box>
					) : null}

					{!loading && result ? (
						<Stack spacing={2.5} sx={{ mt: 2.5 }}>
							<VerdictHero result={result} />

							{!!result.active_offers?.length && (
								<Stack spacing={1.5}>
									<Typography variant="subtitle1" fontWeight={700}>
										Active on this date
									</Typography>
									{result.active_offers.map((offer, index) => (
										<OfferResultCard
											key={`active-${offer.offer_text}-${index}`}
											offer={offer}
											active
										/>
									))}
								</Stack>
							)}

							{!!result.inactive_offers?.length && (
								<Accordion
									defaultExpanded={!result.active_offers?.length}
									sx={{ ...cardSx, "&:before": { display: "none" } }}
								>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography fontWeight={600}>
											Inactive offers ({result.inactive_offers.length})
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Stack spacing={1.5}>
											<Typography variant="body2" color="text.secondary">
												These offers exist for the barcode but are blocked by
												date, store, weekday, or force-close rules.
											</Typography>
											{result.inactive_offers.map((offer, index) => (
												<OfferResultCard
													key={`inactive-${offer.offer_text}-${index}`}
													offer={offer}
													active={false}
												/>
											))}
										</Stack>
									</AccordionDetails>
								</Accordion>
							)}

							{!result.active_offers?.length &&
							!result.inactive_offers?.length ? (
								<Alert severity="info" sx={{ borderRadius: 2 }}>
									No offer schemes were found for this barcode in the current
									discount window.
								</Alert>
							) : null}

							<Divider />

							<Typography variant="caption" color="text.secondary">
								Discount schemes loaded from {result.discount_start_date}
								{result.source ? ` · Source: ${result.source}` : ""}
								{result.cache_hit ? " · cached" : ""}.
							</Typography>
						</Stack>
					) : null}
				</CollapsibleSection>

				<CollapsibleSection
					title="Discount"
					description="List LFS discounts created within a selected date range."
					icon={<HistoryToggleOffOutlinedIcon color="action" />}
					expanded={discountExpanded}
					onChange={(_event, next) => setDiscountExpanded(next)}
				>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Shows Published / Force Closed LFS schemes where created date falls
						between the start and end dates (inclusive).
					</Typography>

					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={1.5}
						alignItems={{ xs: "stretch", sm: "center" }}
						flexWrap="wrap"
						useFlexGap
					>
						<DatePicker
							label="Start date"
							format="DD/MM/YY"
							value={discountStartDate}
							maxDate={dayjs()}
							onChange={(value) => {
								setDiscountStartDate(value || null);
								setDiscountEndDate((prevEnd) =>
									prevEnd?.isValid() &&
									value?.isValid() &&
									prevEnd.isBefore(value, "day")
										? value
										: prevEnd
								);
								setDiscountError("");
							}}
							slotProps={compactDateSlotProps}
						/>
						<DatePicker
							label="End date"
							format="DD/MM/YY"
							value={discountEndDate}
							minDate={
								discountStartDate?.isValid() ? discountStartDate : undefined
							}
							maxDate={dayjs()}
							disabled={!discountStartDate?.isValid()}
							onChange={(value) => {
								setDiscountEndDate(value || null);
								setDiscountError("");
							}}
							slotProps={compactDateSlotProps}
						/>
						<Button
							variant="contained"
							size="small"
							startIcon={<SearchOutlinedIcon />}
							onClick={handleSearchDiscounts}
							disabled={!canSearchDiscounts}
							sx={{
								...primaryButtonSx,
								height: 40,
								px: 2,
							}}
						>
							{discountLoading ? "Loading..." : "Show discounts"}
						</Button>
						{discountResult ? (
							<Button size="small" onClick={handleClearDiscounts}>
								Clear
							</Button>
						) : null}
					</Stack>

					{(discountError || discountRangeError) && (
						<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
							{discountError || discountRangeError}
						</Alert>
					)}

					{discountLoading ? (
						<Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
							<CircularProgress size={32} sx={{ mb: 1.5 }} />
							<Typography variant="body2" color="text.secondary">
								Loading discounts created between{" "}
								{discountStartDate?.isValid()
									? discountStartDate.format("DD MMM YYYY")
									: "start"}{" "}
								and{" "}
								{discountEndDate?.isValid()
									? discountEndDate.format("DD MMM YYYY")
									: "end"}
								...
							</Typography>
						</Stack>
					) : null}

					{!discountLoading && discountResult ? (
						<Stack spacing={2} sx={{ mt: 2.5 }}>
							<Paper
								elevation={0}
								sx={{
									...cardSx,
									p: 2,
									bgcolor: "rgba(33, 33, 33, 0.02)",
								}}
							>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									justifyContent="space-between"
									alignItems={{ xs: "flex-start", sm: "center" }}
									spacing={1}
								>
									<Box>
										<Typography fontWeight={700}>
											{discountResult.count || 0} discount
											{(discountResult.count || 0) === 1 ? "" : "s"} created
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{formatDisplayDate(discountResult.start_date)} →{" "}
											{formatDisplayDate(discountResult.end_date)}
										</Typography>
									</Box>
									<Chip
										icon={<HistoryToggleOffOutlinedIcon />}
										size="small"
										variant="outlined"
										label={`${discountResult.count || 0} found`}
									/>
								</Stack>
							</Paper>

							{sortedDiscountSchemes.length ? (
								sortedDiscountSchemes.map((scheme) => (
									<SchemeRunningCard
										key={scheme.discount_id || scheme.scheme_name}
										scheme={scheme}
									/>
								))
							) : (
								<Alert severity="info" sx={{ borderRadius: 2 }}>
									No LFS discounts were created in this date range.
								</Alert>
							)}
						</Stack>
					) : null}

					{!discountLoading && !discountResult && !discountError ? (
						<Box
							sx={{
								mt: 2,
								py: 5,
								px: 3,
								textAlign: "center",
								borderRadius: 3,
								border: "1px dashed #e0e0e0",
								bgcolor: "rgba(0, 0, 0, 0.02)",
							}}
						>
							<Typography variant="subtitle1" fontWeight={600} gutterBottom>
								Pick a created-date range
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								maxWidth={480}
								mx="auto"
							>
								Use this to see which discount schemes were created in discount
								master between the selected start and end dates.
							</Typography>
						</Box>
					) : null}
				</CollapsibleSection>
			</Stack>
		</LocalizationProvider>
	);
}

export default OfferChecker;
